import type { ReactNode } from 'react';

import { labelsFromCode, linkLabelHost, type UrlSourceClass } from '../../shared/browser/boundUrlSet.ts';

/**
 * The label code derives for a search-result or outside-text link (its
 * host), or null for any other link. A model or a sender writes the text
 * around a link, so that text never names it (injection rule Q3,
 * labelsFromCode). The label says where the link goes, not where it came
 * from: the loader rule (a bound address, the user's own tap) is the
 * defense, not the label (coordinator decision 2026-10-22).
 */
export function boundLinkLabel(url: string | null, source: UrlSourceClass | null): string | null {
  if (!url || !labelsFromCode(source)) return null;
  return linkLabelHost(url);
}

export interface BoundLinkText {
  content: ReactNode;
  title: string | undefined;
  ariaLabel: string | undefined;
}

/**
 * What a link shows. With no code label, the caller's own content. With
 * one, the host replaces the content, unless the caller's content is app
 * copy (`appCopy`): then it stays as is. App copy that is generic ("Open")
 * names the destination itself ("Open on {host}"), so the host shows once.
 */
export function boundLinkText(
  href: string,
  codeLabel: string | null,
  appCopy: boolean,
  children: ReactNode,
  title: string | undefined,
  ariaLabel: string | undefined,
): BoundLinkText {
  if (!codeLabel) return { content: children, title, ariaLabel };
  if (!appCopy) return { content: codeLabel, title: href, ariaLabel: undefined };
  return { content: children, title: href, ariaLabel };
}
